import { BrokerTypes, OfferPublished } from "graphql/__generated__/globalTypes";

import PDFRenderer from "components/core/pdf-renderer";
import Modal from "components/layout/modal";
import config from "config";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { generateURlData } from "utils";
import { parseUrl } from "utils/helpers";
import { urls as __urlss } from "../../../constants";
import PublisherPill from "./PublisherPill";

interface FileItem {
    name: string;
    url: string;
}

// Utility function to parse HTML and extract PDF links
const extractPdfLinks = (htmlContent: string): FileItem[] => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, "text/html");
    const links = doc.querySelectorAll('a[href$=".pdf"]');

    return Array.from(links).map((link) => ({
        name: link.textContent?.trim() || "Unknown PDF",
        url: link.getAttribute("href") || "",
    }));
};

const documentTabKeys = [
    { key: "documents.placing_slip", href: "PLACING_SLIP" },
    { key: "documents.supporting_documents", href: "SUPPORTING_DOCS" },
];

interface SupportingDocsViewProps {
    url: string;
}

const SupportingDocsView: React.FC<SupportingDocsViewProps> = ({ url }) => {
    const { t } = useTranslation();
    const [selectedPdf, setSelectedPdf] = useState<string | null>(null);
    const [pdfFiles, setPdfFiles] = useState<FileItem[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isError, setIsError] = useState(false);
    const [isDirectPdf, setIsDirectPdf] = useState(false);

    useEffect(() => {
        if (!url) {
            setIsLoading(false);
            return;
        }

        const fetchDocuments = async () => {
            try {
                setIsLoading(true);
                setIsError(false);
                const response = await fetch(url);
                const contentType = response.headers.get("content-type");

                if (contentType?.includes("application/pdf")) {
                    setIsDirectPdf(true);
                    setPdfFiles([]);
                } else {
                    const htmlContent = await response.text();
                    const extractedFiles = extractPdfLinks(htmlContent);
                    setPdfFiles(extractedFiles);
                    setIsDirectPdf(false);
                }
            } catch (error) {
                console.error("Error loading supporting documents:", error);
                setIsError(true);
            } finally {
                setIsLoading(false);
            }
        };

        fetchDocuments();
    }, [url]);

    if (isLoading) {
        return (
            <div className="flex justify-center items-center h-[800px]">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mx-auto"></div>
                    <p className="mt-4 text-gray-600">{t("documents.loading")}</p>
                </div>
            </div>
        );
    }

    if (isError) {
        return (
            <div className="flex justify-center items-center h-[800px]">
                <div className="text-center text-red-600">
                    <p>{t("documents.error_loading")}</p>
                </div>
            </div>
        );
    }

    if (isDirectPdf) {
        return <PDFRenderer url={url} height={800} />;
    }

    if (pdfFiles.length === 0) {
        return (
            <div className="flex justify-center items-center flex-col h-[800px]">
                <svg
                    className="w-20 h-20 text-gray-300 mb-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24">
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1}
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                </svg>
                <h1 className="text-sm md:text-xl font-semibold mt-2">
                    {t("documents.no_attachments")}
                </h1>
                <p className="text-xs md:text-lg font-extralight">
                    {t("documents.try_adding")}
                </p>
            </div>
        );
    }

    return (
        <div className="h-[800px] flex gap-4">
            <div className="w-1/3 border-r pr-4 overflow-y-auto">
                <h3 className="font-semibold mb-4 text-gray-700">
                    {t("documents.available_documents")}
                </h3>
                <div className="space-y-2">
                    {pdfFiles.map((file, index) => (
                        <button
                            key={index}
                            onClick={() => setSelectedPdf(file.url)}
                            className={`w-full p-3 rounded-lg border transition-colors text-left ${
                                selectedPdf === file.url
                                    ? "bg-blue-50 border-blue-300 text-blue-700"
                                    : "bg-gray-50 border-gray-200 hover:bg-gray-100"
                            }`}>
                            <div className="font-medium text-sm truncate">{file.name}</div>
                        </button>
                    ))}
                </div>
            </div>

            <div className="flex-1 h-full">
                {selectedPdf ? (
                    <PDFRenderer url={selectedPdf} height={800} />
                ) : (
                    <div className="flex items-center justify-center h-full text-gray-500">
                        <div className="text-center">
                            <svg
                                className="w-16 h-16 mx-auto mb-4 text-gray-300"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24">
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={1}
                                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                />
                            </svg>
                            <p>{t("documents.select_document")}</p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

interface DocumentViewerContainerProps {
    open: boolean;
    setOpen: (open: boolean) => void;
    offerId: string;
    offer?: OfferPublished;
}

const DocumentViewerContainer = ({
    open,
    setOpen,
    offerId,
    offer,
}: DocumentViewerContainerProps) => {
    const { t } = useTranslation();
    const [activeTab, setActiveTab] = useState("PLACING_SLIP");

    const getDocumentUrl = () => {
        const baseParams = {
            offer_id: offerId,
            period_from:
                offer?.offer?.offer_detail?.period_of_insurance_from || "",
            period_to: offer?.offer?.offer_detail?.period_of_insurance_to || "",
            lang: "en",
        };

        if (activeTab === "PLACING_SLIP") {
            if (offer?.published_by?.toLowerCase().includes("kek")) {
                return parseUrl(
                    `${config.asset.kekUri}/generate_placing_slip/${generateURlData({
                        ...baseParams,
                        emp_id: 1,
                    })}`,
                    { forceHttps: true, defaultProtocol: "https" },
                );
            }
            if (offer?.published_by?.toLowerCase() === "placeit") {
                return parseUrl(
                    `${config.asset.placeItUri}/generate_placing_slip/${generateURlData({
                        ...baseParams,
                        emp_id: 1,
                    })}`,
                    { forceHttps: true, defaultProtocol: "https" },
                );
            }

            return parseUrl(
                `${__urlss[BrokerTypes.Visal]}/generate_placing_slip/${generateURlData(
                    {
                        ...baseParams,
                        emp_id: 1,
                    },
                )}`,
                { forceHttps: true, defaultProtocol: "https" },
            );
        }

        if (activeTab === "SUPPORTING_DOCS") {
            if (offer?.published_by?.toLowerCase().includes("kek")) {
                return parseUrl(
                    `${config.asset.kekUri}/generate-broker-supporting-docs/${offerId}`,
                    {
                        defaultProtocol: "https",
                        forceHttps: true,
                    },
                );
            }
            if (offer?.published_by?.toLowerCase() === "placeit") {
                return parseUrl(
                    `${config.asset.placeItUri}/api/v1/offer-docs-content/${offer.offer.id}`,
                    {
                        defaultProtocol: "https",
                        forceHttps: true,
                    },
                );
            }
            if (offer?.offer?.offer_from === "PLACEIT") {
                return parseUrl(
                    `${config.asset.placeItUri}/generate-supporting-docs/${offer.offer.place_it_id}`,
                    {
                        defaultProtocol: "https",
                        forceHttps: true,
                    },
                );
            } else if (offer?.offer?.offer_from === "INTERNAL") {
                return parseUrl(
                    `${config.asset.visalUri}/generate-broker-supporting-docs/${offerId}`,
                    {
                        defaultProtocol: "https",
                        forceHttps: true,
                    },
                );
            }
        }

        return "";
    };

    const handleTabChange = (tabKey: string) => {
        setActiveTab(tabKey);
    };

    return (
        <Modal
            open={open}
            setOpen={setOpen}
            title={t("modal_titles.offer_documents")}
            description={
                <span>
                    {t("documents.view_description")}{" "}
                    <PublisherPill publishedBy={offer?.published_by} />
                </span>
            }
            size="6xl">
            <div className="flex flex-col h-full">
                <div className="border-b border-gray-200">
                    <nav className="-mb-px flex space-x-8 px-4" aria-label="Tabs">
                        {documentTabKeys.map((tab) => (
                            <button
                                key={tab.href}
                                onClick={() => handleTabChange(tab.href)}
                                className={`${
                                    activeTab === tab.href
                                        ? "border-green-500 text-green-600"
                                        : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                                aria-current={activeTab === tab.href ? "page" : undefined}>
                                {t(tab.key)}
                            </button>
                        ))}
                    </nav>
                </div>

                <div className="flex-1 p-4">
                    {activeTab === "SUPPORTING_DOCS" ? (
                        <SupportingDocsView url={getDocumentUrl()} />
                    ) : (
                        <PDFRenderer url={getDocumentUrl()} />
                    )}
                </div>
            </div>
        </Modal>
    );
};

export default DocumentViewerContainer;
